import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import { CourseStatus } from '@ts-core/utils/Constants';

import { removeItemFromCache, addItemToCache } from '@ts-core/core/utils/apolloUtils';
import { useMutation } from '@ts-core/lib/apollo';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';

import { getCoursesQuery, GetCoursesQueryResult } from '../graphql/queries/getCoursesQuery';
import { createCourseMutation, CreateCourseMutationResult, CreateCourseMutationVariables } from '../graphql/mutations/createCourseMutation';
import { updateCourseMutation, UpdateCourseMutationResult, UpdateCourseMutationVariables } from '../graphql/mutations/updateCourseMutation';
import { removeCourseMutation, RemoveCourseMutationResult, RemoveCourseMutationVariables } from '../graphql/mutations/removeCourseMutation';
import {
  toggleCoursePeriodMutation,
  ToggleCoursePeriodMutationResult,
  ToggleCoursePeriodMutationVariables,
} from '../graphql/mutations/toggleCoursePeriodMutation';
import { Course } from '../types';
import { CourseFormInput } from '../types/form';

export interface CourseActionHookOutput {
  onCreateCourse: (input: CourseFormInput, onCompleted?: () => void) => void;
  creatingCourse: boolean;

  onUpdateCourse: (courseId: string, input: CourseFormInput) => void;
  updatingCourse: boolean;

  onTogglePeriod: (course: Course) => void;
  changingPeriod: boolean;

  onRemoveCourse: (course: Course) => void;
}

export default function useCourseActions(): CourseActionHookOutput {
  const apolloClient = useApolloClient();
  const { showSuccessNotification } = useSnackbarNotifications();

  /**
   * Create Course
   */
  const [createCourse, { loading: creatingCourse }] = useMutation<CreateCourseMutationResult, CreateCourseMutationVariables>(createCourseMutation, {
    notifyError: true,
    update: addItemToCache<CreateCourseMutationResult, GetCoursesQueryResult>({
      query: getCoursesQuery,
      queryName: 'courses',
      mutationName: 'course',
    }),
  });
  const onCreateCourse = useCallback(
    (input: CourseFormInput, onSuccess?: () => void) => {
      createCourse({ variables: { input }, onCompleted: onSuccess });
    },
    [createCourse]
  );

  /**
   * Update Course
   */
  const [updateCourse, { loading: updatingCourse }] = useMutation<UpdateCourseMutationResult, UpdateCourseMutationVariables>(updateCourseMutation, {
    notifyError: true,
    notifySuccess: true,
  });
  const onUpdateCourse = useCallback(
    (courseId: string, input: CourseFormInput) => {
      updateCourse({ variables: { courseId, input } });
    },
    [updateCourse]
  );

  /**
   * Toggle Course Period
   */
  const [togglePeriod, { loading: changingPeriod }] = useMutation<ToggleCoursePeriodMutationResult, ToggleCoursePeriodMutationVariables>(
    toggleCoursePeriodMutation,
    {
      notifyError: true,
      onCompleted: () => {
        showSuccessNotification();
        apolloClient.refetchQueries({ include: 'all' });
      },
    }
  );
  const onTogglePeriod = useCallback(
    (course: Course) => {
      const isPeriodOpen = course.periodStatus === CourseStatus.Active;
      togglePeriod({
        variables: { courseId: course._id, status: isPeriodOpen ? 'close' : 'open' },
      });
    },
    [togglePeriod]
  );

  /**
   * Remove Course
   */
  const [removeCourse] = useMutation<RemoveCourseMutationResult, RemoveCourseMutationVariables>(removeCourseMutation, {
    notifyError: true,
    update: removeItemFromCache<RemoveCourseMutationResult, GetCoursesQueryResult>({
      query: getCoursesQuery,
      queryName: 'courses',
      mutationName: 'course',
      idAccessor: '_id',
    }),
  });
  const onRemoveCourse = useCallback(
    (course: Course) => {
      removeCourse({
        variables: { courseId: course._id },
        optimisticResponse: { course },
      });
    },
    [removeCourse]
  );

  return {
    onCreateCourse,
    creatingCourse,

    onUpdateCourse,
    updatingCourse,

    onTogglePeriod,
    changingPeriod,

    onRemoveCourse,
  };
}
